import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import { getEndOfDayReportDetails } from "@/api/reporting";
import { useReportTills } from "./use-report-tills";
import {
  buildReportCsvName,
  downloadCsv,
  formatCurrency,
  formatCount,
  getTodayInputDate,
  parseTillId,
} from "./reporting-utils";
import hasPermission from "@/hooks/hasPermission";

const buildPageNumbers = (currentPage, lastPage) => {
  if (lastPage <= 1) return [1];
  if (lastPage <= 5) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  const pages = new Set([1, lastPage, currentPage]);
  if (currentPage > 2) pages.add(currentPage - 1);
  if (currentPage < lastPage - 1) pages.add(currentPage + 1);

  const sortedPages = Array.from(pages)
    .filter((page) => page >= 1 && page <= lastPage)
    .sort((a, b) => a - b);

  const result = [];
  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      result.push("...");
    }
    result.push(page);
  });

  return result;
};

function EndOfDayReportZDetails() {
  const [tillFilter, setTillFilter] = useState("all");
  const [perPage, setPerPage] = useState("10");
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { tills } = useReportTills();

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, tillFilter, perPage]);

  useEffect(() => {
    let isActive = true;

    const fetchReport = async () => {
      setIsLoading(true);
      const tillId = parseTillId(tillFilter);
      const response = await getEndOfDayReportDetails({
        date: selectedDate || undefined,
        till_id: tillId,
        page: currentPage,
        per_page: Number(perPage),
      });

      if (!isActive) return;

      if (response?.status === "success") {
        const payload = response?.data || {};
        const normalizedRows = (payload.data || []).map((item, index) => ({
          id: item.order_id || item.id || item.orderId || `#${index + 1}`,
          items:
            item.items_count ??
            item.number_of_items ??
            item.items ??
            item.quantity ??
            0,
          cnp: item.cnp ?? item.online ?? item.cnp_total ?? 0,
          pbl: item.pbl ?? item.pay_by_link ?? item.pbl_total ?? 0,
          card: item.card ?? item.card_total ?? 0,
          cash: item.cash ?? item.cash_total ?? 0,
          total: item.total_value ?? item.total ?? item.total_price ?? 0,
        }));

        const nextPagination = payload.pagination || {};
        setRows(normalizedRows);
        const lastPage = Math.max(1, Number(nextPagination.last_page ?? 1));
        setPagination({
          total: Number(nextPagination.total ?? 0),
          per_page: Number(nextPagination.per_page ?? perPage),
          current_page: Math.min(
            Math.max(1, Number(nextPagination.current_page ?? currentPage)),
            lastPage
          ),
          last_page: lastPage,
        });
      } else {
        toast.error(response?.message || "Failed to load report details.");
        setRows([]);
        setPagination({
          total: 0,
          per_page: Number(perPage),
          current_page: 1,
          last_page: 1,
        });
      }

      setIsLoading(false);
    };

    fetchReport();

    return () => {
      isActive = false;
    };
  }, [selectedDate, tillFilter, perPage, currentPage]);

  const handleExportCsv = () => {
    if (!rows.length) {
      toast.error("No data available to export.");
      return;
    }

    const tillId = parseTillId(tillFilter);
    const fileName = buildReportCsvName(
      "end-of-day-z-details",
      selectedDate,
      tillId
    );
    const csvRows = rows.map((row) => [
      row.id,
      formatCount(row.items),
      formatCurrency(row.cnp),
      formatCurrency(row.pbl),
      formatCurrency(row.card),
      formatCurrency(row.cash),
      formatCurrency(row.total),
    ]);
    downloadCsv(
      fileName,
      ["Order Id", "Number of Items", "CNP", "PBL", "Card", "Cash", "Total value"],
      csvRows
    );
  };

  const pageNumbers = useMemo(
    () => buildPageNumbers(pagination.current_page, pagination.last_page),
    [pagination.current_page, pagination.last_page]
  );

  return (
    <Layout2
      pageName={{ icon: true, title: "End of Day Report/Z (Details)" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Reporting", path: "/reporting" },
        {
          name: "End of Day Report/Z (Details)",
          path: "/reporting/end-of-day-z-details",
        },
      ]}
    >
      <div className="px-4 pb-12">
        <div className="text-sm font-semibold text-cwhite">Search By Date</div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-max-content">
              <Input
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                className="bg-cblack3 text-sm"
              />
            </div>
            <Select value={tillFilter} onValueChange={setTillFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Filter by Till" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Till</SelectItem>
                {tills.length === 0 ? (
                  <SelectItem value="no-tills" disabled>
                    No tills available
                  </SelectItem>
                ) : (
                  tills.map((till) => (
                    <SelectItem key={till.id} value={String(till.id)}>
                      {till.device_name || `Till-${till.id}`}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {hasPermission("reporting.reporting.download_end_of_day_report_details") && (
              <button
                type="button"
                onClick={handleExportCsv}
                className="flex h-9 items-center gap-2 rounded-md bg-cblack6 px-4 text-sm text-cwhite"
              >
                Export as CSV
                <Download size={16} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-cwhite3">
            <span>Show</span>
            <Select value={perPage} onValueChange={setPerPage}>
              <SelectTrigger className="h-9 w-20 bg-cblack6">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-md border border-cblack6">
          <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr_1fr_1fr] bg-cblack6 px-4 py-3 text-sm font-semibold text-cwhite">
            <span>Order ID</span>
            <span className="text-center">Number of Items</span>
            <span className="text-center">CNP</span>
            <span className="text-center">PBL</span>
            <span className="text-center">Card</span>
            <span className="text-center">Cash</span>
            <span className="text-right">Total Value</span>
          </div>
          {isLoading ? (
            <div className="border-t border-cblack6 px-4 py-6 text-center text-sm text-cwhite3">
              Loading report data...
            </div>
          ) : rows.length === 0 ? (
            <div className="border-t border-cblack6 px-4 py-6 text-center text-sm text-cwhite3">
              No data found for the selected filters.
            </div>
          ) : (
            rows.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr_1fr_1fr] items-center border-t border-cblack6 px-4 py-3 text-sm text-cwhite3"
              >
                <span>{row.id}</span>
                <span className="text-center">{formatCount(row.items)}</span>
                <span className="text-center">{formatCurrency(row.cnp)}</span>
                <span className="text-center">{formatCurrency(row.pbl)}</span>
                <span className="text-center">{formatCurrency(row.card)}</span>
                <span className="text-center">{formatCurrency(row.cash)}</span>
                <span className="text-right">{formatCurrency(row.total)}</span>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <div className="flex items-center gap-2 text-sm text-cwhite3">
            <button
              type="button"
              disabled={pagination.current_page <= 1}
              onClick={() =>
                setCurrentPage((prev) => Math.max(prev - 1, 1))
              }
              className="flex h-8 w-8 items-center justify-center rounded border border-cblack6 bg-cblack5 text-cwhite3 disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            {pageNumbers.map((page, index) => (
              <button
                key={`${page}-${index}`}
                type="button"
                disabled={page === "..."}
                onClick={() =>
                  typeof page === "number" && setCurrentPage(page)
                }
                className={`flex h-8 min-w-[32px] items-center justify-center rounded border border-cblack6 px-2 ${page === pagination.current_page
                    ? "bg-cyellow text-cblack"
                    : "bg-cblack5 text-cwhite3"
                  } ${page === "..." ? "cursor-default" : ""}`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              disabled={pagination.current_page >= pagination.last_page}
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(prev + 1, pagination.last_page)
                )
              }
              className="flex h-8 w-8 items-center justify-center rounded border border-cblack6 bg-cblack5 text-cwhite3 disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default EndOfDayReportZDetails;
